import React, { useState } from 'react';
import { Users, User, ChevronDown } from 'lucide-react';

const CompactViewSwitcher = ({ currentUser, familyUsers, switchUser, isCollapsed }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  if (!currentUser || !familyUsers || familyUsers.length === 0) {
    return null;
  }
  
  // Only actual parents should be able to use the view switcher
  // Check if user is a parent (either currently in parent view OR has originalParent flag indicating they're a parent viewing as kid)
  const isActualParent = currentUser?.role === 'parent' || currentUser?.type === 'parent' || currentUser?.isParent || currentUser?.originalParent;
  if (!isActualParent) {
    return null;
  }
  
  const isParentView = currentUser?.isParent || currentUser?.type === 'parent';
  const isKidView = !isParentView;
  const children = familyUsers?.filter(user => user.type !== 'parent' && !user.isParent) || [];
  
  const handleSwitchUser = (user) => {
    if (switchUser && typeof switchUser === 'function') {
      switchUser(user);
      setIsOpen(false);
    }
  };
  
  const getCurrentDisplayName = () => {
    if (isParentView) return 'Parent';
    return currentUser?.displayName || currentUser?.name || currentUser?.first_name || 'Kid';
  };
  
  const getCurrentIcon = () => {
    return isParentView ? <Users className="w-4 h-4" /> : <User className="w-4 h-4" />;
  };
  
  // Collapsed state - just show icon
  if (isCollapsed) {
    return (
      <div className="relative">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`flex items-center justify-center w-14 h-14 rounded-xl transition-all shadow-sm hover:shadow-md group ${
            isKidView 
              ? 'bg-gradient-to-br from-violet-100 to-violet-50 hover:from-violet-200 hover:to-violet-100' 
              : 'bg-gradient-to-br from-slate-100 to-slate-50 hover:from-slate-200 hover:to-slate-100'
          }`}
          title={`Current view: ${getCurrentDisplayName()}`}
        >
          <div className="relative">
            {getCurrentIcon()}
            <div className={`absolute -top-1 -right-1 w-2 h-2 rounded-full opacity-90 animate-pulse ${
              isKidView ? 'bg-violet-500' : 'bg-teal-500'
            }`} />
          </div>
        </button>
        
        {/* Dropdown menu for collapsed state */}
        {isOpen && (
          <div className="absolute bottom-full left-0 mb-2 w-48 bg-white rounded-lg shadow-lg border border-stone-200 py-1 z-50 animate-in fade-in slide-in-from-bottom-2 duration-200">
            <div className="px-3 py-2 text-xs text-stone-500 font-medium uppercase tracking-wide border-b border-stone-100">
              Switch View
            </div>
            
            {/* Parent option */}
            <button
              onClick={() => {
                const parentUser = familyUsers.find(user => user.type === 'parent' || user.isParent);
                if (parentUser) handleSwitchUser(parentUser);
              }}
              className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-stone-50 transition-colors ${
                isParentView ? 'bg-stone-100 font-medium' : ''
              }`}
            >
              <Users className="w-4 h-4" />
              <span>Parent View</span>
            </button>
            
            {/* Children options */}
            {children.map((child) => (
              <button
                key={child.id}
                onClick={() => handleSwitchUser(child)}
                className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-stone-50 transition-colors ${
                  !isParentView && currentUser?.id === child.id ? 'bg-stone-100 font-medium' : ''
                }`}
              >
                <User className="w-4 h-4" />
                <span>{child.displayName || child.name || child.first_name}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }
  
  // Expanded state - show dropdown button
  return (
    <div className="relative">
      <div className={`text-xs font-medium text-app-subtle uppercase tracking-wide mb-2`}>
        View As
      </div>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl transition-all shadow-sm hover:shadow-md text-sm font-medium group ${
          isKidView 
            ? 'bg-gradient-to-br from-violet-100 to-violet-50 hover:from-violet-200 hover:to-violet-100' 
            : 'bg-gradient-to-br from-slate-100 to-slate-50 hover:from-slate-200 hover:to-slate-100'
        }`}
      >
        <div className="flex items-center gap-2">
          <div className="relative">
            {getCurrentIcon()}
            <div className={`absolute -top-1 -right-1 w-2 h-2 rounded-full opacity-90 animate-pulse ${
              isKidView ? 'bg-violet-500' : 'bg-teal-500'
            }`} />
          </div>
          <span className="text-slate-800">{getCurrentDisplayName()} View</span>
        </div>
        <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {/* Dropdown menu */}
      {isOpen && (
        <div className="absolute bottom-full left-0 right-0 mb-2 bg-white rounded-lg shadow-lg border border-stone-200 py-1 z-50 animate-in fade-in slide-in-from-bottom-2 duration-200">
          <div className="px-3 py-2 text-xs text-stone-500 font-medium uppercase tracking-wide border-b border-stone-100">
            Switch View
          </div>
          
          {/* Parent option */}
          <button
            onClick={() => {
              const parentUser = familyUsers.find(user => user.type === 'parent' || user.isParent);
              if (parentUser) handleSwitchUser(parentUser);
            }}
            className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-stone-50 transition-colors ${
              isParentView ? 'bg-stone-100 font-medium' : ''
            }`}
          >
            <Users className="w-4 h-4" />
            <span>Parent View</span>
          </button>
          
          {/* Children options */}
          {children.map((child) => (
            <button
              key={child.id}
              onClick={() => handleSwitchUser(child)}
              className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-stone-50 transition-colors ${
                !isParentView && currentUser?.id === child.id ? 'bg-stone-100 font-medium' : ''
              }`}
            >
              <User className="w-4 h-4" />
              <span>{child.displayName || child.name || child.first_name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default CompactViewSwitcher;